import type {
  IDaySchedule,
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../../../../types/schedule';
import { isFreeSlot } from '../../../../../types/schedule';
import type { Class, User } from '../../../../../types/tournament';
import { ClassFormatEnum, ClassMatchType } from '../../../../../types/enums';
import { getDisplayName } from '../../../../../utils/userDisplayName';
import {
  getDateString,
  getHhmmTimeString,
  getMatchDurationFromConstraints,
} from '../schedule.helpers';
import {
  buildMapAxisTicks,
  computeMapBlockPercents,
} from './schedulePrintMapLayout';

export const PRINT_SLOT_MINUTES = 15;
const MS_PER_MINUTE = 60000;
const MAX_MATCH_SPAN_MINUTES = 24 * 60;
const MAP_COURTS_PER_BAND = 20;
const MAP_COURT_BAND_THRESHOLD = 25;

export type SchedulePrintLayout = 'map' | 'list';

export type SchedulePrintMapOrientation = 'timeOnX' | 'timeOnY';

export type SchedulePrintCourtRange = {
  startCourtIndex: number;
  endCourtIndex: number;
};

export type SchedulePrintMapBuildOptions = {
  orientation?: SchedulePrintMapOrientation;
  courtRanges?: SchedulePrintCourtRange[] | null;
  listCategoryIds?: number[] | null;
};

export type SchedulePrintSortContext = {
  classIdToObject: Record<number, Class>;
};

export type SchedulePrintFiltersMeta = {
  filterSummary: string;
};

export type SchedulePrintMapBlock = {
  matchId: string;
  courtRowIndex: number;
  leftPercent: number;
  widthPercent: number;
  topPercent: number;
  heightPercent: number;
  shortLabel: string;
  classColor: string;
  className: string;
};

export type SchedulePrintMapLegendEntry = {
  classId: number;
  className: string;
  color: string;
};

export type SchedulePrintMapCourtBand = {
  startIndex: number;
  endIndex: number;
};

export type SchedulePrintMapDay = {
  dayKey: number;
  dayTitle: string;
  dayDate: string;
  isFirstDay: boolean;
  isRangeBreak: boolean;
  rangeLabel: string;
  orientation: SchedulePrintMapOrientation;
  hourTicks: { label: string; leftPercent: number; topPercent: number }[];
  courtRows: { courtId: number; courtName: string }[];
  blocks: SchedulePrintMapBlock[];
  legend: SchedulePrintMapLegendEntry[];
  courtBands: SchedulePrintMapCourtBand[];
};

export type SchedulePrintListRow = {
  startTime: string;
  endTime: string;
  court: string;
  code: string;
  className: string;
  homeTeam: string;
  awayTeam: string;
  matchId: string;
};

export type SchedulePrintListDay = {
  dayKey: number;
  dayTitle: string;
  dayDate: string;
  isFirstDay: boolean;
  rows: SchedulePrintListRow[];
};

export type SchedulePrintModel = {
  tournamentName: string;
  generatedAtLabel: string;
  filterSummary: string;
  statusLabel: 'published' | 'draft' | '';
  mapDays: SchedulePrintMapDay[];
  listDays: SchedulePrintListDay[];
  maxCourtCount: number;
  useLandscape: boolean;
  usePortrait: boolean;
  matchCount: number;
};

const getMatchStartMs = (match: IMatchScheduleItem) =>
  new Date(match.scheduleDate!).getTime();

const getMatchEndMs = (
  match: IMatchScheduleItem,
  startMs: number,
  dayEndMs: number,
  constraints: IScheduleConstraints | null
) => {
  let endMs = match.scheduleEndDate
    ? new Date(match.scheduleEndDate).getTime()
    : startMs +
      (match.duration || getMatchDurationFromConstraints(match, constraints) || 20) *
        MS_PER_MINUTE;

  if (!Number.isFinite(endMs) || endMs <= startMs) {
    endMs =
      startMs +
      (match.duration || getMatchDurationFromConstraints(match, constraints) || 20) *
        MS_PER_MINUTE;
  }

  const maxEndMs = Math.min(
    dayEndMs,
    startMs + MAX_MATCH_SPAN_MINUTES * MS_PER_MINUTE
  );
  return Math.min(endMs, maxEndMs);
};

const getFallbackDayBounds = (day: IDaySchedule) => {
  const scheduledItems = day.courts
    .flatMap((court) => court.matches)
    .filter((item) => item.scheduleDate);
  const firstStartMs = Math.min(
    ...scheduledItems.map((item) => new Date(item.scheduleDate!).getTime())
  );
  const lastEndMs = Math.max(
    ...scheduledItems.map((item) => {
      const startMs = new Date(item.scheduleDate!).getTime();
      if (item.scheduleEndDate) return new Date(item.scheduleEndDate).getTime();
      return startMs + (item.duration || PRINT_SLOT_MINUTES) * MS_PER_MINUTE;
    })
  );

  return {
    dayStartMs: Number.isFinite(firstStartMs) ? firstStartMs : Date.now(),
    dayEndMs: Number.isFinite(lastEndMs)
      ? lastEndMs
      : Date.now() + 60 * MS_PER_MINUTE,
  };
};

export function getPrintRoundShortCode(
  item: IMatchScheduleItem,
  classObject?: Class
): string {
  if (!classObject) return '';
  const isGroupFormat = classObject.format === ClassFormatEnum.GROUP;
  const isFinal = item.type === ClassMatchType.FINAL;
  const isWinnersBracket = item.bracket === 1;

  if (isFinal) {
    if (item.isGold) {
      return item.isAdditional ? 'AF' : 'F';
    }
    return isGroupFormat ? 'B' : 'LF';
  }
  if (isGroupFormat) {
    return `R${item.type}`;
  }
  return isWinnersBracket ? `W${item.type}` : `L${item.type}`;
}

export function getPrintGroupSortKey(
  item: IMatchScheduleItem,
  ctx: SchedulePrintSortContext
): string {
  const groupName = item.tournamentGroup?.name ?? '';
  const roundCode = getPrintRoundShortCode(item, ctx.classIdToObject[item.classId]);
  return `${groupName}|${roundCode}`;
}

export function getPrintBlockShortLabel(
  item: IMatchScheduleItem,
  ctx: SchedulePrintSortContext
): string {
  const groupName = item.tournamentGroup?.name ?? '';
  if (groupName.length === 1) return groupName;
  if (groupName.length > 0) return groupName.slice(0, 4);
  return getPrintRoundShortCode(item, ctx.classIdToObject[item.classId]);
}

export function getPrintListCodeLabel(
  item: IMatchScheduleItem,
  ctx: SchedulePrintSortContext
): string {
  const groupName = item.tournamentGroup?.name ?? '';
  if (groupName.length > 0) return groupName;
  return getPrintRoundShortCode(item, ctx.classIdToObject[item.classId]);
}

export function compareScheduleItemsForPrint(
  a: IMatchScheduleItem,
  b: IMatchScheduleItem,
  ctx: SchedulePrintSortContext
): number {
  const tA = new Date(a.scheduleDate!).getTime();
  const tB = new Date(b.scheduleDate!).getTime();
  if (tA !== tB) return tA - tB;

  const cA = a.court?.id ?? 0;
  const cB = b.court?.id ?? 0;
  if (cA !== cB) return cA - cB;

  const nA = a.court?.name ?? '';
  const nB = b.court?.name ?? '';
  const nameCmp = nA.localeCompare(nB, undefined, { numeric: true });
  if (nameCmp !== 0) return nameCmp;

  const gCmp = getPrintGroupSortKey(a, ctx).localeCompare(getPrintGroupSortKey(b, ctx), undefined, {
    numeric: true,
  });
  if (gCmp !== 0) return gCmp;

  return (a.matchId ?? '').localeCompare(b.matchId ?? '');
}

export function sortScheduledMatchesForPrint(
  matches: IMatchScheduleItem[],
  ctx: SchedulePrintSortContext
): IMatchScheduleItem[] {
  return [...matches].sort((a, b) => compareScheduleItemsForPrint(a, b, ctx));
}

export function getScheduleMatchRoundLabel(
  item: IMatchScheduleItem,
  classObject?: Class
): string {
  if (!classObject) return '';
  const isGroupStage =
    !!item.tournamentGroup?.name || classObject.format === ClassFormatEnum.GROUP;
  const isFinal = item.type === ClassMatchType.FINAL;
  const isWinnersBracket = item.bracket === 1;

  if (isFinal) {
    return item.isGold
      ? item.isAdditional
        ? 'Add. Final'
        : 'Final'
      : isGroupStage
        ? 'Bronze'
        : 'Losers Final';
  }
  return (
    'Round ' +
    (isGroupStage
      ? item.type + (item.tournamentGroup ? ' ' + item.tournamentGroup.name : '')
      : isWinnersBracket
        ? 'W' + item.type
        : 'L' + item.type)
  );
}

export function formatSchedulePlayerName(player: User | null | undefined): string {
  return getDisplayName(player);
}

export function getScheduleMatchPlayersLabel(item: IMatchScheduleItem): string {
  const home = [formatSchedulePlayerName(item.homePlayer1), formatSchedulePlayerName(item.homePlayer2)]
    .filter(Boolean)
    .join(', ');
  const away = [formatSchedulePlayerName(item.awayPlayer1), formatSchedulePlayerName(item.awayPlayer2)]
    .filter(Boolean)
    .join(', ');
  if (!home && !away) return '';
  if (!home) return away;
  if (!away) return home;
  return `${home} vs ${away}`;
}

export function getScheduleTeamLabel(
  item: IMatchScheduleItem,
  side: 'home' | 'away'
): string {
  if (side === 'home') {
    return item.homeTeam?.name || item.homeTeamPlaceholder || 'TBD';
  }
  return item.awayTeam?.name || item.awayTeamPlaceholder || 'TBD';
}

const CLASS_COLORS = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc948',
  '#b07aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ac',
];

export const getClassColor = (classId: number) => CLASS_COLORS[classId % CLASS_COLORS.length];

const getDayTimeBounds = (
  day: IDaySchedule,
  constraints: IScheduleConstraints | null
) => {
  const dayConstraints = constraints?.days.find((d) => d.dayKey === day.dayKey);
  const fallbackBounds = getFallbackDayBounds(day);
  return {
    dayStartMs: dayConstraints
      ? new Date(dayConstraints.startTime).getTime()
      : fallbackBounds.dayStartMs,
    dayEndMs: dayConstraints
      ? new Date(dayConstraints.endTime).getTime()
      : fallbackBounds.dayEndMs,
  };
};

const buildMapCourtBands = (courtCount: number): SchedulePrintMapCourtBand[] => {
  if (courtCount <= MAP_COURT_BAND_THRESHOLD) {
    return [{ startIndex: 0, endIndex: courtCount }];
  }
  const bands: SchedulePrintMapCourtBand[] = [];
  for (let i = 0; i < courtCount; i += MAP_COURTS_PER_BAND) {
    bands.push({ startIndex: i, endIndex: Math.min(i + MAP_COURTS_PER_BAND, courtCount) });
  }
  return bands;
};

const formatCourtRangeLabel = (
  courtRows: { courtName: string }[],
  startIndex: number,
  endIndex: number
) => {
  const startName = courtRows[startIndex]?.courtName ?? String(startIndex + 1);
  const endName = courtRows[endIndex]?.courtName ?? String(endIndex + 1);
  if (startIndex === endIndex) return startName;
  return `${startName} – ${endName}`;
};

export function courtRangesOverlap(
  ranges: SchedulePrintCourtRange[]
): boolean {
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const a = ranges[i];
      const b = ranges[j];
      if (a.startCourtIndex <= b.endCourtIndex && b.startCourtIndex <= a.endCourtIndex) {
        return true;
      }
    }
  }
  return false;
}

const buildSchedulePrintMapDay = (
  day: IDaySchedule,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<number, Class>,
  dayTitle: string,
  isFirstDay: boolean,
  isRangeBreak: boolean,
  rangeLabel: string,
  orientation: SchedulePrintMapOrientation,
  courtRange: SchedulePrintCourtRange | null
): SchedulePrintMapDay => {
  const ctx: SchedulePrintSortContext = { classIdToObject };
  const { dayStartMs, dayEndMs } = getDayTimeBounds(day, constraints);
  const timelineMs = Math.max(dayEndMs - dayStartMs, MS_PER_MINUTE);
  const dayConstraints = constraints?.days.find((d) => d.dayKey === day.dayKey);
  const dayDate =
    dayConstraints?.startTime != null
      ? getDateString(dayConstraints.startTime, timeZone)
      : day.courts
          .flatMap((c) => c.matches)
          .find((m) => !isFreeSlot(m) && m.scheduleDate)?.scheduleDate
        ? getDateString(
            (day.courts
              .flatMap((c) => c.matches)
              .find((m) => !isFreeSlot(m) && m.scheduleDate) as IMatchScheduleItem)
              .scheduleDate!,
            timeZone
          )
        : '';

  const allCourtRows = day.courts.map((c) => ({
    courtId: c.court.id,
    courtName: c.court.name,
  }));

  const rangeStart = courtRange?.startCourtIndex ?? 0;
  const rangeEnd = courtRange?.endCourtIndex ?? allCourtRows.length - 1;
  const courtRows = allCourtRows.slice(rangeStart, rangeEnd + 1);
  const courtIndexById = new Map(
    allCourtRows.map((c, i) => [c.courtId, i])
  );

  const matches = sortScheduledMatchesForPrint(
    day.courts.flatMap((court) =>
      court.matches.filter(
        (item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate
      )
    ),
    ctx
  ).filter((match) => {
    const idx = courtIndexById.get(match.court?.id ?? 0);
    if (idx == null) return false;
    return idx >= rangeStart && idx <= rangeEnd;
  });

  const legendByClass = new Map<number, SchedulePrintMapLegendEntry>();
  const blocks: SchedulePrintMapBlock[] = matches.map((match) => {
    const startMs = getMatchStartMs(match);
    const endMs = getMatchEndMs(match, startMs, dayEndMs, constraints);
    const classObject = classIdToObject[match.classId];
    const className = classObject?.name ?? '';
    const classColor = getClassColor(match.classId);
    if (!legendByClass.has(match.classId)) {
      legendByClass.set(match.classId, {
        classId: match.classId,
        className,
        color: classColor,
      });
    }
    const globalCourtIndex = courtIndexById.get(match.court?.id ?? 0) ?? 0;
    const courtRowIndex = globalCourtIndex - rangeStart;
    const percents = computeMapBlockPercents(
      startMs,
      endMs,
      dayStartMs,
      timelineMs,
      courtRowIndex,
      orientation
    );
    return {
      matchId: match.matchId,
      courtRowIndex: percents.courtRowIndex,
      leftPercent: percents.leftPercent,
      widthPercent: percents.widthPercent,
      topPercent: percents.topPercent,
      heightPercent: percents.heightPercent,
      shortLabel: getPrintBlockShortLabel(match, ctx),
      classColor,
      className,
    };
  });

  const legend = [...legendByClass.values()].sort((a, b) =>
    a.className.localeCompare(b.className, undefined, { numeric: true })
  );

  const resolvedRangeLabel =
    rangeLabel ||
    (courtRange
      ? formatCourtRangeLabel(allCourtRows, rangeStart, rangeEnd)
      : '');

  return {
    dayKey: day.dayKey,
    dayTitle,
    dayDate,
    isFirstDay,
    isRangeBreak,
    rangeLabel: resolvedRangeLabel,
    orientation,
    hourTicks: buildMapAxisTicks(dayStartMs, dayEndMs, timeZone, getHhmmTimeString),
    courtRows,
    blocks,
    legend,
    courtBands: buildMapCourtBands(courtRows.length),
  };
};

const buildSchedulePrintListDay = (
  day: IDaySchedule,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<number, Class>,
  dayTitle: string,
  isFirstDay: boolean,
  listCategoryIds?: number[] | null
): SchedulePrintListDay => {
  const ctx: SchedulePrintSortContext = { classIdToObject };
  const { dayEndMs } = getDayTimeBounds(day, constraints);
  const dayConstraints = constraints?.days.find((d) => d.dayKey === day.dayKey);
  const dayDate =
    dayConstraints?.startTime != null
      ? getDateString(dayConstraints.startTime, timeZone)
      : day.courts
          .flatMap((c) => c.matches)
          .find((m) => !isFreeSlot(m) && m.scheduleDate)?.scheduleDate
        ? getDateString(
            (day.courts
              .flatMap((c) => c.matches)
              .find((m) => !isFreeSlot(m) && m.scheduleDate) as IMatchScheduleItem)
              .scheduleDate!,
            timeZone
          )
        : '';

  const matches = sortScheduledMatchesForPrint(
    day.courts.flatMap((court) =>
      court.matches.filter(
        (item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate
      )
    ),
    ctx
  );

  const categoryFilter =
    listCategoryIds && listCategoryIds.length > 0 ? new Set(listCategoryIds) : null;
  const filteredMatches = categoryFilter
    ? matches.filter((match) => categoryFilter.has(Number(match.classId)))
    : matches;

  const rows: SchedulePrintListRow[] = filteredMatches.map((match) => {
    const startMs = getMatchStartMs(match);
    const endMs = getMatchEndMs(match, startMs, dayEndMs, constraints);
    const classObject = classIdToObject[match.classId];
    return {
      startTime: getHhmmTimeString(match.scheduleDate!, timeZone),
      endTime: getHhmmTimeString(new Date(endMs).toISOString(), timeZone),
      court: match.court?.name ?? '',
      code: getPrintListCodeLabel(match, ctx),
      className: classObject?.name ?? '',
      homeTeam: getScheduleTeamLabel(match, 'home'),
      awayTeam: getScheduleTeamLabel(match, 'away'),
      matchId: match.matchId,
    };
  });

  return {
    dayKey: day.dayKey,
    dayTitle,
    dayDate,
    isFirstDay,
    rows,
  };
};

const expandMapDaysWithRanges = (
  printSchedule: ITournamentSchedule,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<number, Class>,
  dayTitleForKey: (dayKey: number) => string,
  orientation: SchedulePrintMapOrientation,
  courtRanges: SchedulePrintCourtRange[] | null
): SchedulePrintMapDay[] => {
  const sections: SchedulePrintMapDay[] = [];
  let isFirstSection = true;

  printSchedule.days.forEach((day, dayIndex) => {
    const dayTitle = dayTitleForKey(day.dayKey);
    const ranges =
      courtRanges && courtRanges.length > 0
        ? courtRanges
        : [null as SchedulePrintCourtRange | null];

    ranges.forEach((range, rangeIndex) => {
      const isFirstDay = dayIndex === 0 && rangeIndex === 0;
      const isRangeBreak = !isFirstSection;
      const rangeLabel =
        range != null
          ? formatCourtRangeLabel(
              day.courts.map((c) => ({ courtName: c.court.name })),
              range.startCourtIndex,
              range.endCourtIndex
            )
          : '';

      sections.push(
        buildSchedulePrintMapDay(
          day,
          constraints,
          timeZone,
          classIdToObject,
          dayTitle,
          isFirstDay,
          isRangeBreak,
          rangeLabel,
          orientation,
          range
        )
      );
      isFirstSection = false;
    });
  });

  return sections;
};

export function buildSchedulePrintModel(
  printSchedule: ITournamentSchedule | null,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<number, Class>,
  filtersMeta: SchedulePrintFiltersMeta,
  tournamentName: string,
  generatedAtLabel: string,
  statusLabel: 'published' | 'draft' | '',
  dayTitleForKey: (dayKey: number) => string,
  mapOptions?: SchedulePrintMapBuildOptions
): SchedulePrintModel | null {
  if (!printSchedule || printSchedule.days.length === 0) {
    return null;
  }

  const orientation = mapOptions?.orientation ?? 'timeOnX';
  const courtRanges = mapOptions?.courtRanges ?? null;
  const listCategoryIds = mapOptions?.listCategoryIds ?? null;

  const mapDays = expandMapDaysWithRanges(
    printSchedule,
    constraints,
    timeZone,
    classIdToObject,
    dayTitleForKey,
    orientation,
    courtRanges
  );

  const listDays = printSchedule.days.map((day, index) =>
    buildSchedulePrintListDay(
      day,
      constraints,
      timeZone,
      classIdToObject,
      dayTitleForKey(day.dayKey),
      index === 0,
      listCategoryIds
    )
  );

  const matchCount = listDays.reduce((sum, day) => sum + day.rows.length, 0);
  const maxCourtCount = Math.max(
    0,
    ...printSchedule.days.map((d) => d.courts.length)
  );

  const usePortrait =
    orientation === 'timeOnY' && maxCourtCount <= 6;
  const useLandscape = !usePortrait && maxCourtCount >= 6;

  return {
    tournamentName,
    generatedAtLabel,
    filterSummary: filtersMeta.filterSummary,
    statusLabel,
    mapDays,
    listDays,
    maxCourtCount,
    useLandscape,
    usePortrait,
    matchCount,
  };
}
